import { ref } from "vue";
import { EmailValidator } from "commons-validator-js";

export function useValidation() {
  // Holds error messages and sub messages for specified fields.
  /**
   * errors :{
   * 'Question' : { 'errorMsg' : ' Question Body must not be empty. }
   * }
   */
  const errors = ref<Record<string, { errorMsg: string }>>({});

  const hasError = (fieldName: string) => {
    return !!errors.value[fieldName];
  };

  const getErrorMsg = (fieldName: string) => {
    const error = errors.value[fieldName];
    return error?.errorMsg ?? "";
  };

  const clearValidationError = () => {
    Object.keys(errors.value).forEach((key) => delete errors.value[key]);
  };

  const handleCloseError = (fieldName: string) => {
    // remove error for specific field from map
    delete errors.value[fieldName];
  };

  const setFieldError = (fieldName: string, errorMsg: string) => {
    errors.value[fieldName] = { errorMsg };
  };

  // Validate number input (default only allow whole number)
  const validateNumberInput = (
    fieldValue: string | number,
    params: {
      min: number;
      max: number;
      steps?: number;
      allowDecimal?: boolean;
    },
  ): boolean => {
    const value = Number.parseFloat(String(fieldValue));
    if (isNaN(value)) return false;

    const min = params.min;
    const max = params.max;
    const steps = params?.steps ?? 1;
    const allowDecimal = params?.allowDecimal ?? false;

    if (!allowDecimal && value % 1 !== 0) return false;

    if (value < min || value > max) return false;

    // check if value is allowed based on incremental or decremental value
    if (steps > 1 && value % steps !== 0) return false;

    return true;
  };

  const validateEmail = (value: string): boolean => {
    const validator = new EmailValidator();
    return validator.isValid(value);
  };

  function validateHalfMarkInput(
    value: number | string | null,
    minMax: { min: number; max: number },
  ): boolean {
    const numValue = Number(value);
    if (isNaN(numValue) || value === null || value === "") return false;

    if (numValue < minMax.min || numValue > minMax.max) return false;

    // check the decimal part
    const decimalPart = numValue % 1;
    return decimalPart === 0 || decimalPart === 0.5;
  }

  return {
    errors,
    hasError,
    getErrorMsg,
    clearValidationError,
    handleCloseError,
    setFieldError,
    validateNumberInput,
    validateEmail,
    validateHalfMarkInput,
  };
}
